import { useState, FC, useEffect } from 'react';
import {
  useMediaQuery,
  Button,
  Divider,
  ListItemText,
  Collapse,
  ListItemSecondaryAction,
  ListItemButton,
  IconButton
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Check, Add, ExpandLess, ExpandMore } from '@mui/icons-material';
import clsx from 'clsx';
import { IColorExclusions } from '../../../../../models';
import {
  updateNeighborhoodSeriesSelectedColorExclusionOptions,
  getColorSchemes
} from '../../../../../fetch';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { ExcludedColorOptions } from './excluded-color-options';

interface IExcludedColorOptionList {
  selections: number[];
  colorOption: IColorExclusions;
  refetch: () => void;
}

export const ExcludedColorOptionList: FC<IExcludedColorOptionList> = ({
  selections,
  colorOption,
  refetch
}) => {
  const classes = useStyles();
  const { neighborhoodId, selectedSeriesId }: { neighborhoodId: string; selectedSeriesId: string } =
    useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [isExcluded, setIsExcluded] = useState(selections?.includes(colorOption.colorSchemeID));
  const [open, setOpen] = useState(false);
  const [isLoadingColorExclusionOptions, setIsLoadingColorExclusionOptions] =
    useState<boolean>(false);
  const [colorUris, setColorUris] = useState();

  useEffect(() => {
    setIsExcluded(selections?.includes(colorOption.colorSchemeID));
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selections]);

  const isMobile = useMediaQuery('(max-width: 960px)');

  const handleClick = async () => {
    if (isExcluded) {
      await removeColorOption();
    } else {
      await createColorOption();
    }
  };

  const removeColorOption = async () => {
    try {
      await updateNeighborhoodSeriesSelectedColorExclusionOptions(
        neighborhoodId,
        selectedSeriesId,
        selections.filter(selection => selection !== colorOption.colorSchemeID)
      );
      enqueueSnackbar(`Exclusion removed.`, {
        variant: 'success'
      });
      refetch();
    } catch (error) {
      enqueueSnackbar(`Error removing exclusion ${colorOption.code}}.`, {
        variant: 'error'
      });
    }
  };

  const createColorOption = async () => {
    try {
      await updateNeighborhoodSeriesSelectedColorExclusionOptions(
        neighborhoodId,
        selectedSeriesId,
        [...selections, colorOption.colorSchemeID]
      );
      enqueueSnackbar(`Successfully excluded code ${colorOption.code}!`, {
        variant: 'success'
      });
      refetch();
    } catch (error) {
      enqueueSnackbar(`Error excluding code ${colorOption.code}.`, {
        variant: 'error'
      });
    }
  };

  const fetchColorOptionUris = async () => {
    setIsLoadingColorExclusionOptions(true);
    try {
      const colors = await getColorSchemes(colorOption.colorSchemeID);
      setColorUris(colors);
    } catch (error) {
      enqueueSnackbar(`Error getting color options`, {
        variant: 'error'
      });
    } finally {
      setIsLoadingColorExclusionOptions(false);
    }
  };

  return (
    <>
      <ListItemButton
        classes={{
          root: classes.listItemWithCats
        }}
        onClick={() => {
          if (open) {
            return setOpen(!open);
          }
          fetchColorOptionUris();
          return setOpen(!open);
        }}
      >
        <IconButton
          color="default"
          className={classes.openButton}
          aria-label="open"
          onClick={() => {
            return setOpen(!open);
          }}
          size="large"
        >
          {open ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
        <ListItemText primary={<div>Code: {colorOption.code}</div>} />
        <ListItemSecondaryAction className={classes.buttonWrapper}>
          <Button
            color="primary"
            variant={isExcluded ? 'contained' : 'text'}
            startIcon={isExcluded ? <Check /> : <Add />}
            className={clsx(classes.editButton, classes.selected)}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              handleClick();
            }}
          >
            {isMobile ? '' : `${isExcluded ? 'Excluded' : 'Exclude'}`}
          </Button>
        </ListItemSecondaryAction>
      </ListItemButton>
      <Divider />
      <Collapse in={open} timeout="auto" unmountOnExit className={classes.nested}>
        <ExcludedColorOptions
          colorUris={colorUris}
          isLoadingColorExclusionOptions={isLoadingColorExclusionOptions}
        />
      </Collapse>
    </>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: theme.palette.background.paper
  },
  nested: {
    paddingLeft: theme.spacing(2)
  },
  listItem: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5)
  },
  listItemWithCats: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(1)
  },
  nestedListItemWithCats: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(2)
  },
  buttonWrapper: {
    display: 'flex',
    alignItems: 'center'
  },
  editButton: {
    marginRight: 8,
    padding: 0
  },
  openButton: {
    padding: 0
  },
  selected: {
    fontWeight: 'bold',
    padding: `2px 8px`,
    borderRadius: 5,
    marginRight: 0
  },
  emphasis: {
    fontWeight: 'bold',
    textDecoration: 'underline'
  },
  listItemText: {
    maxWidth: 'calc(100% - 140px)'
  }
}));
